import {
  Badge,
  BadgeVariantEnum,
  ChevronDownIcon,
  CloseXIcon,
  FontSizeEnum,
  IconButton,
  IconButtonColorEnum,
  IconButtonSizeEnum,
  IconSizeEnum,
  PauseIsolatedIcon,
  PlayIsolatedIcon,
  Tooltip,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import { MutableRefObject, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { TSupport } from '@/features/programs/api/get-supports';
import { EVisibility } from '@/types/types';
import { formatHMS } from '@/utils/time';

import { UNDEFINED_SUPPORT_ID } from '../constants';
import { TimeKeepingButtonStateEnum, TimeKeepingSupportRegistry } from '../types';
import { computeRecordsDuration } from '../utils/compute-records-duration';
import { sortSupportsByAlias } from '../utils/support-alias';

import { TimeKeepingSupportRecord } from './TimeKeepingSupportRecord';

export type AccordionEntry = {
  key: string;
  compositeId: string;
  supportPresenceId: string;
  liElement: HTMLLIElement;
};

export type TimeKeepingButtonState = {
  state: TimeKeepingButtonStateEnum;
  recordIndex?: number;
};

export const DEFAULT_BUTTON_STATE: TimeKeepingButtonState = {
  state: TimeKeepingButtonStateEnum.INACTIVE,
};

const getButtonColor = (state: TimeKeepingButtonStateEnum, isVideoPlaying: boolean) => {
  if (state === TimeKeepingButtonStateEnum.INACTIVE) return IconButtonColorEnum.DEFAULT;
  return isVideoPlaying ? IconButtonColorEnum.SECONDARY : IconButtonColorEnum.PRIMARY;
};

type TimeKeepingAccordionProps = {
  compositeId: string;
  supports: TimeKeepingSupportRegistry[];
  buttonStates: Record<string, TimeKeepingButtonState>;
  openAccordions: Map<string, AccordionEntry>;
  allSupports: TSupport[];
  playerVideoIsPlaying: boolean;
  playerVideoCurrentTime: number;
  getDateTimeCurrentPresence: (time: number) => Date | null | undefined;
  liRefs: MutableRefObject<Map<string, HTMLLIElement>>;
  onToggleAccordion: (
    key: string,
    el: HTMLElement,
    compositeId: string,
    supportPresenceId: string
  ) => void;
  onPlayPause: (
    compositeId: string,
    supportPresenceId: string,
    currentRecordsLength: number
  ) => void;
  onResetSupport: (compositeId: string, supportPresenceId: string) => void;
  onRemoveRecord: (compositeId: string, supportPresenceId: string, recordIndex: number) => void;
  onToggleVisibility: (
    compositeId: string,
    supportPresenceId: string,
    recordIndex: number,
    currentVisibility: EVisibility,
    el: HTMLElement
  ) => void;
};

export function TimeKeepingAccordion({
  compositeId,
  supports,
  buttonStates,
  openAccordions,
  allSupports,
  playerVideoIsPlaying,
  playerVideoCurrentTime,
  getDateTimeCurrentPresence,
  liRefs,
  onToggleAccordion,
  onPlayPause,
  onResetSupport,
  onRemoveRecord,
  onToggleVisibility,
}: TimeKeepingAccordionProps) {
  const { t, i18n } = useTranslation();

  const undefinedLabel = t('Sin definir');
  const sortedSupports = useMemo(
    () => sortSupportsByAlias(supports, allSupports, undefinedLabel, i18n.language),
    [supports, allSupports, undefinedLabel, i18n.language]
  );

  return (
    <ol data-id="card-widget-supports-table" className="text-[13px]">
      {sortedSupports.map(({ registry, alias }) => {
        const accordionKey = `${compositeId}:${registry.presenceId}`;

        const buttonState = buttonStates[registry.presenceId] ?? DEFAULT_BUTTON_STATE;
        const isButtonActive = buttonState.state === TimeKeepingButtonStateEnum.ACTIVE;
        const liveElapsed = (() => {
          if (!isButtonActive || buttonState.recordIndex === undefined) return 0;
          const activeRec = registry.records[buttonState.recordIndex];
          if (!activeRec || activeRec.datetimeEnd !== '0') return 0;
          const now = getDateTimeCurrentPresence(playerVideoCurrentTime) ?? new Date();
          const diff = now.getTime() - new Date(activeRec.datetimeStart).getTime();
          return diff > 0 ? diff / 1000 : 0;
        })();
        const duration = formatHMS(computeRecordsDuration(registry.records) + liveElapsed);

        return (
          <li
            key={registry.presenceId}
            ref={el => {
              if (el) liRefs.current.set(accordionKey, el);
              else liRefs.current.delete(accordionKey);
            }}
            className="mb-1 border-b border-dotted border-gray-200"
          >
            <div className="relative z-100 flex items-start justify-between gap-2 px-1 py-2">
              <div className="flex min-w-0 flex-1 items-center gap-1">
                <IconButton
                  color={getButtonColor(buttonState.state, playerVideoIsPlaying)}
                  size={IconButtonSizeEnum.XS}
                  disabled={registry.presenceId === UNDEFINED_SUPPORT_ID}
                  onClick={() =>
                    onPlayPause(compositeId, registry.presenceId, registry.records.length)
                  }
                  onPointerDown={e => e.stopPropagation()}
                  className="mt-0.5 min-h-4 min-w-4 shrink-0"
                >
                  <PlayIsolatedIcon
                    size={IconSizeEnum['2XS']}
                    className={clsx(isButtonActive && 'hidden')}
                  />
                  <PauseIsolatedIcon
                    size={IconSizeEnum['2XS']}
                    className={clsx(!isButtonActive && 'hidden')}
                  />
                </IconButton>
                <button
                  data-id="support-dropdown-button"
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-1 text-left text-[13px]"
                  onPointerDown={e => e.stopPropagation()}
                  onClick={e =>
                    onToggleAccordion(
                      accordionKey,
                      e.currentTarget,
                      compositeId,
                      registry.presenceId
                    )
                  }
                >
                  <Typography
                    data-id="support-alias"
                    className="min-w-0 flex-1 break-words"
                    fontSize={FontSizeEnum.Xs}
                  >
                    {alias}
                  </Typography>
                  <ChevronDownIcon
                    size={IconSizeEnum['2XS']}
                    className={clsx(
                      'shrink-0 transition-transform',
                      openAccordions.has(accordionKey) && 'rotate-180'
                    )}
                  />
                </button>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Badge data-id="support-records" variant={BadgeVariantEnum.DarkFilled}>
                  {registry.records.length}
                </Badge>
                <Typography fontSize={FontSizeEnum.Xs}>{duration}</Typography>
                <Tooltip help={t('Reiniciar tiempos')} grow>
                  <IconButton
                    color={IconButtonColorEnum.NONE_PRIMARY}
                    size={IconButtonSizeEnum.XS}
                    onPointerDown={e => e.stopPropagation()}
                    onClick={() => onResetSupport(compositeId, registry.presenceId)}
                    disabled={registry.records.length === 0 && !isButtonActive}
                  >
                    <CloseXIcon size={IconSizeEnum['2XS']} />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          </li>
        );
      })}
      {/* Portals for expanded accordion rows — rendered after <li> elements so targets exist in DOM */}
      {Array.from(openAccordions.values())
        .filter(acc => acc.compositeId === compositeId)
        .map(accordion => {
          const registry = supports.find(s => s.presenceId === accordion.supportPresenceId);
          if (!registry) return null;
          return createPortal(
            <div
              key={accordion.key}
              className="relative z-[100] max-h-48 overflow-auto bg-bg-primary-10"
            >
              {registry.records.map((record, idx) => {
                const bs = buttonStates[accordion.supportPresenceId] ?? DEFAULT_BUTTON_STATE;
                const isActive =
                  bs.state === TimeKeepingButtonStateEnum.ACTIVE && bs.recordIndex === idx;
                return (
                  <TimeKeepingSupportRecord
                    key={idx}
                    compositeId={accordion.compositeId}
                    supportPresenceId={accordion.supportPresenceId}
                    record={record}
                    idx={idx}
                    isActive={isActive}
                    playerVideoCurrentTime={playerVideoCurrentTime}
                    getDateTimeCurrentPresence={getDateTimeCurrentPresence}
                    onRemove={onRemoveRecord}
                    onToggleVisibility={onToggleVisibility}
                  />
                );
              })}
            </div>,
            accordion.liElement
          );
        })}
    </ol>
  );
}
